import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { trackSignUp } from "../analytics";
import "./SignInModal.scss";

interface SignInModalProps {
  isVisible: boolean;
  onClose: () => void;
  onSignInSuccess: () => void;
}

const SignInModal: React.FC<SignInModalProps> = ({
  isVisible,
  onClose,
  onSignInSuccess,
}) => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [missingCredentials] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [referralSource, setReferralSource] = useState("");

  const { signIn, signUp, resetPassword } = useAuth();

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (missingCredentials) {
      return;
    }

    setError(null);
    setSuccessMessage(null);
    setLoading(true);

    try {
      const { error } = await signIn(email, password);

      if (error) {
        setError(error.message);
      } else {
        setSuccessMessage("Sign in successful!");
        setEmail("");
        setPassword("");
        setTimeout(() => {
          onSignInSuccess();
          onClose();
        }, 1000);
      }
    } catch (err) {
      setError("An unexpected error occurred. Please try again.");
      console.error("Sign in error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (missingCredentials) {
      return;
    }

    // Validation
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters long.");
      return;
    }

    setError(null);
    setSuccessMessage(null);
    setLoading(true);

    try {
      const { error, userAlreadyExists } = await signUp(email, password);

      if (error) {
        setError(error.message);
      } else if (userAlreadyExists) {
        setError("An account with this email already exists.");
      } else {
        trackSignUp(referralSource);
        setSuccessMessage(
          "Account created! Please check your email to verify your address before signing in.",
        );
        setEmail("");
        setPassword("");
        setConfirmPassword("");
        setReferralSource("");
        setTimeout(() => {
          setIsSignUp(false);
        }, 4000);
      }
    } catch (err) {
      setError("An unexpected error occurred. Please try again.");
      console.error("Sign up error:", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setError(null);
    setSuccessMessage(null);
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setReferralSource("");
    setIsSignUp(!isSignUp);
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail) {
      setError("Please enter your email address");
      return;
    }

    setError(null);
    setSuccessMessage(null);
    setResetLoading(true);

    try {
      const { error } = await resetPassword(resetEmail);

      if (error) {
        setError(error.message);
      } else {
        setSuccessMessage(
          "Password reset email sent! Check your inbox for the recovery link.",
        );
        setResetEmail("");
        setTimeout(() => {
          setShowPasswordReset(false);
          setSuccessMessage(null);
        }, 4000);
      }
    } catch (err) {
      setError("An unexpected error occurred. Please try again.");
      console.error("Password reset error:", err);
    } finally {
      setResetLoading(false);
    }
  };

  if (!isVisible) return null;

  return (
    <>
      <div className="signin-modal-overlay" onClick={onClose} />
      <div className="signin-modal-container">
        <div className="signin-modal-card">
          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
          <h2>
            {showPasswordReset
              ? "Reset Password"
              : isSignUp
                ? "Create Account"
                : "Sign In"}
          </h2>
          {error && <div className="error-message">{error}</div>}
          {successMessage && (
            <div className="success-message">{successMessage}</div>
          )}

          {!showPasswordReset ? (
            <form onSubmit={isSignUp ? handleSignUp : handleSignIn}>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  disabled={loading || missingCredentials}
                />
              </div>
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  disabled={loading || missingCredentials}
                />
                {!isSignUp && (
                  <button
                    type="button"
                    className="forgot-password-link"
                    onClick={() => {
                      setShowPasswordReset(true);
                      setError(null);
                      setSuccessMessage(null);
                    }}
                    disabled={loading}
                  >
                    Forgot Password?
                  </button>
                )}
              </div>
              {isSignUp && (
                <>
                  <div className="form-group">
                    <label htmlFor="confirm-password">Confirm Password</label>
                    <input
                      id="confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm your password"
                      required
                      disabled={loading || missingCredentials}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="referral-source">
                      How did you hear about us? (Optional)
                    </label>
                    <select
                      id="referral-source"
                      value={referralSource}
                      onChange={(e) => setReferralSource(e.target.value)}
                      disabled={loading || missingCredentials}
                      className="referral-select"
                    >
                      <option value="">Select an option</option>
                      <option value="search">
                        Search Engine (Google, Bing, etc.)
                      </option>
                      <option value="social">Social Media</option>
                      <option value="friend">Friend or Colleague</option>
                      <option value="blog">Blog or Article</option>
                      <option value="conference">Conference or Event</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </>
              )}
              <button
                type="submit"
                disabled={loading || missingCredentials}
                className="signin-button"
              >
                {loading
                  ? isSignUp
                    ? "Creating Account..."
                    : "Signing In..."
                  : isSignUp
                    ? "Create Account"
                    : "Sign In"}
              </button>
            </form>
          ) : (
            <form onSubmit={handlePasswordReset}>
              <div className="form-group">
                <label htmlFor="reset-email">Email Address</label>
                <input
                  id="reset-email"
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  disabled={resetLoading}
                />
              </div>
              <button
                type="submit"
                disabled={resetLoading}
                className="signin-button"
              >
                {resetLoading ? "Sending..." : "Send Reset Email"}
              </button>
              <button
                type="button"
                className="back-link"
                onClick={() => {
                  setShowPasswordReset(false);
                  setResetEmail("");
                  setError(null);
                  setSuccessMessage(null);
                }}
                disabled={resetLoading}
              >
                Back to Sign In
              </button>
            </form>
          )}

          {!showPasswordReset && (
            <div className="toggle-mode">
              <span>
                {isSignUp
                  ? "Already have an account? "
                  : "Don't have an account? "}
              </span>
              <button
                type="button"
                onClick={toggleMode}
                className="toggle-mode-btn"
                disabled={loading}
              >
                {isSignUp ? "Sign In" : "Sign Up"}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SignInModal;
